import { makeAutoObservable, runInAction } from 'mobx'
import axios, { type CancelTokenSource, CanceledError } from 'axios'
import { debounce } from 'lodash'
import { logger } from 'shared/lib'
import { UsersApi } from 'entities/Users'
import { User } from 'entities/Users/model/User'
import { type IUser } from 'entities/Users/model/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type ISearchDropdownStoreOptions } from 'widgets/SearchDropdown/store/types'
import { SearchTypesEnum } from 'widgets/SearchDropdown/model/types'

type UserMode = 'assignee' | 'members'

export class SearchDropdownUsersStore {
  private _data: User[] = []
  private _loading = false
  private _search = ''
  private _total = 0
  private _page = 1
  private _perPage = 10
  private _maxLimit: number | null = null
  private _disableClosing = false
  private _debounceFetch
  private _selectedItemsMapIds: Map<number, number> = new Map()
  private _options: ISearchDropdownStoreOptions | null = null
  private _cancelTokenSource: CancelTokenSource | null = null
  private _mode: UserMode

  constructor(private _entities: IEntitiesStore, mode: UserMode) {
    makeAutoObservable(this)
    this._mode = mode
    this._debounceFetch = debounce(this.init, 500)
  }

  getUnassignedItem(search: string): User | undefined {
    if (!this.includeUnassigned) return undefined

    const unassignedItem = {
      id: 0,
      name: 'Unassigned',
      avatarInfo: { icon: 'userLine', color: 'white' },
    } as unknown as User

    if (search && !unassignedItem.name.toLowerCase().includes(search.toLowerCase())) {
      return undefined
    }

    return unassignedItem
  }

  get mode(): UserMode {
    return this._mode
  }

  get type(): SearchTypesEnum {
    return this._mode === 'assignee' ? SearchTypesEnum.assignee : SearchTypesEnum.members
  }

  get title(): string {
    return this._mode === 'assignee' ? 'Assignee' : 'Member'
  }

  get pluralTitle(): string {
    return this._mode === 'assignee' ? 'assignees' : 'members'
  }

  get placeholder(): string {
    return this._mode === 'assignee' ? 'Search assignee' : 'Search member'
  }

  get includeUnassigned(): boolean {
    return this._mode === 'assignee'
  }

  get paramKey(): 'assignees' | 'members' {
    return this._mode === 'assignee' ? 'assignees' : 'members'
  }

  get firstSelectedItemId() {
    const id = this._selectedItemsMapIds.entries().next().value?.[0]

    if (!id) return null

    return id
  }

  get selectedItemsIds() {
    return Array.from(this._selectedItemsMapIds.values())
  }

  get selectedItemsListIds() {
    return Array.from(this._selectedItemsMapIds).map((item) => item[1])
  }

  get hasSelectedItems() {
    return !!this.selectedItemsCount
  }

  get selectedItemsCount() {
    return this._selectedItemsMapIds.size
  }

  get hasItems() {
    return !!this._data.length
  }

  get first3Items() {
    return this._data.slice(0, 3)
  }

  get itemsList() {
    return this._data
  }

  get itemsListIds() {
    return this._data.map((item) => item.id)
  }

  get hasMore() {
    const loadedCount = this._page * this._perPage

    return loadedCount < this._total
  }

  initCancelTokenSource = () => {
    this._cancelTokenSource?.cancel()
    this._cancelTokenSource = axios.CancelToken.source()
  }

  resetCancelTokenSource = () => {
    this._cancelTokenSource?.cancel()
  }

  initOptions = (options: ISearchDropdownStoreOptions) => {
    this._options = options
  }

  isSelected = (id: number) => {
    const isSelected = this._selectedItemsMapIds.get(id)

    return isSelected === undefined ? false : true
  }

  selectTypeItem = (item: IUser) => {
    this.toggleTypeItem(item)
    this._options?.resetSearch?.()
  }

  resetType = () => {
    this._selectedItemsMapIds.clear()
    this.reset()
  }

  toggleTypeItem = (item: IUser) => {
    const id = Number(item.id)

    if (this._options?.selectedMode) {
      if (this._selectedItemsMapIds.has(id)) {
        this._selectedItemsMapIds.delete(id)
      } else {
        this._selectedItemsMapIds.set(id, id)
      }
    }

    this._options?.toggleTypeItem?.(item)
  }

  setSelectedItemsIds = (ids: number[]) => {
    this._selectedItemsMapIds = new Map(ids.map((id) => [id, id]))
  }

  reset = () => {
    this._data = []
    this._loading = false
    this._search = ''
    this._total = 0
    this._page = 1
    this._cancelTokenSource?.cancel()
    this._debounceFetch.cancel()
  }

  handleSearch = (value: string) => {
    this._search = value
    this.resetCancelTokenSource()
    this._debounceFetch()
  }

  setMaxLimit = (maxLimit: number | null) => {
    this._maxLimit = maxLimit
  }

  setDisableClosing = (disableClosing: boolean) => {
    this._disableClosing = disableClosing
  }

  loadMore = async () => {
    try {
      runInAction(() => {
        this._loading = true
        this.initCancelTokenSource()
      })

      const { data } = await UsersApi.getUsersSearch(
        {
          term: this._search || '*',
          page: this._page + 1,
          per_page: this._perPage,
        },
        { cancelToken: this._cancelTokenSource?.token }
      )

      this._entities.usersStore.addItems(data.results || [])

      const results = data.results || []
      const items = results.map((item) => new User(item))

      runInAction(() => {
        this._data = [...this._data, ...items]
        this._total = data.total
        this._page += 1
      })
    } catch (e) {
      if (e instanceof CanceledError) return

      logger.error(e)
    } finally {
      this._loading = false
    }
  }

  init = async () => {
    try {
      runInAction(() => {
        this._loading = true
        this._data = []
        this._page = 1
        this.initCancelTokenSource()
      })

      const { data } = await UsersApi.getUsersSearch(
        {
          term: this._search || '*',
          page: this._page,
          per_page: this._perPage,
          exclude: this.selectedItemsIds,
        },
        {
          cancelToken: this._cancelTokenSource?.token,
        }
      )

      this._entities.usersStore.addItems(data.results || [])

      const results = data.results || []
      const items = results.map((item) => new User(item))

      runInAction(() => {
        this._data = items
        this._total = data.total
      })
    } catch (e) {
      if (e instanceof CanceledError) return

      logger.error(e)
    } finally {
      this._loading = false
    }
  }

  get search() {
    return this._search
  }

  get loading() {
    return this._loading
  }

  get options() {
    return this._options
  }

  get selectedItemsMapIds() {
    return this._selectedItemsMapIds
  }

  get total() {
    return this._total
  }

  get disableClosing() {
    return this._disableClosing
  }

  get maxLimit() {
    return this._maxLimit
  }
}
